import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { listContentTypes, buildSlugIdMap } from "api/endpoints/contentTypes";

// ContentTypesContext — loads the global content-type list once at app start
// and exposes the slug → content_type_id map. List/filter endpoints take the
// UUID, not the slug (quirk #3), so every content screen depends on this.

const ContentTypesContext = createContext(null);

export function ContentTypesProvider({ children }) {
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [types, setTypes] = useState([]);
  const [slugIdMap, setSlugIdMap] = useState({});

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const items = await listContentTypes();
      setTypes(items);
      setSlugIdMap(buildSlugIdMap(items));
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(
    () => ({ status, types, slugIdMap, reload: load }),
    [status, types, slugIdMap, load]
  );

  return <ContentTypesContext.Provider value={value}>{children}</ContentTypesContext.Provider>;
}

ContentTypesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useContentTypes() {
  const ctx = useContext(ContentTypesContext);
  if (!ctx) {
    throw new Error("useContentTypes must be used within a ContentTypesProvider");
  }
  return ctx;
}

// Resolve a content-type path slug (e.g. "article") to its UUID.
export function useContentTypeId(pathSlug) {
  const { slugIdMap } = useContentTypes();
  return slugIdMap[pathSlug];
}
